import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiService } from '../../shared/services/api.service';
import { Observable } from 'rxjs';
import { FloorResponse, FloorsResponse } from '../responses/floor.response';
import { Floor } from '../models/floor.model';
import { Parameter } from '../../shared/models/parameter.model';
import { environment } from '../../../../environments/environment';
import { FloorQueryBuilder } from '../../shared/classes/floor.query-builder.class';
import { CascadeService } from '../../shared/services/cascade.service';

@Injectable()
export class FloorsService {
  private resource = 'floors';
  private queryBuilder: FloorQueryBuilder;

  constructor(private http: HttpClient, private cascadeService: CascadeService) {
    if (environment.cordova) {
      this.queryBuilder = new FloorQueryBuilder();
    }
  }

  /**
   * Get all floors.
   */
  getAllFloors(
    limit: number = 15,
    includes: string[] = null,
    sorting: string = null,
    appends: string[] = null,
    parameters: Parameter[] = [],
  ): Observable<FloorsResponse> {
    let params = new HttpParams();

    params = params.set('limit', limit.toFixed(0));

    if (includes) {
      params = params.set('include', includes.join(','));
    }

    if (appends) {
      params = params.set('append', appends.join(','));
    }

    if (sorting) {
      params = params.set('sort', sorting);
    }

    parameters.forEach(parameter => {
      if (parameter.value) {
        params = params.set(`filter[${parameter.field}]`, `${parameter.value}`);
      }
    });

    return this.http.get<FloorsResponse>(`${ApiService.ApiUrl}/${this.resource}`, { params });
  }

  /**
   * Find floor by id.
   */
  findFloorById(floorId: string, includes: string[] = null): Observable<FloorResponse> {
    let params = new HttpParams();

    if (includes) {
      params = params.set('include', includes.join(','));
    }

    return !environment.cordova
      ? this.http.get<FloorResponse>(`${ApiService.ApiUrl}/${this.resource}/${floorId}`, { params })
      : this.queryBuilder.findWithIncludes(+floorId, includes);
  }

  /**
   * Create new user floor.
   */
  createFloor(floor: Floor): Observable<FloorResponse> {
    return !environment.cordova
      ? this.http.post<FloorResponse>(`${ApiService.ApiUrl}/${this.resource}`, floor)
      : this.queryBuilder.create(floor);
  }

  /**
   * Update user floor.
   */
  updateFloor(floor: Floor): Observable<FloorResponse> {
    return !environment.cordova
      ? this.http.patch<FloorResponse>(`${ApiService.ApiUrl}/${this.resource}/${floor.id}`, floor)
      : this.queryBuilder.update(floor);
  }

  /**
   * Delete user floor.
   */
  deleteFloor(floor: Floor): Observable<any> {
    if (environment.cordova) {
      this.queryBuilder = new FloorQueryBuilder();
      this.cascadeService.floor(floor);
    }

    return !environment.cordova
      ? this.http.delete(`${ApiService.ApiUrl}/${this.resource}/${floor.id}`)
      : this.queryBuilder.where('id', '=', floor.id).delete(floor);
  }

  /**
   * Sync floor.
   */
  syncFloor(payload): Observable<any> {
    return this.http.post<any>(`${ApiService.ApiUrl}/${this.resource}/sync`, payload);
  }
}
